const mirutoWorkers = () =>
  (window.MIRUTO_CONFIG && window.MIRUTO_CONFIG.integrations && window.MIRUTO_CONFIG.integrations.workers) ||
  {};

/* ── 定数 ── */
const STORE_LABELS = {
  miruto: '公式ストア',
  amazon: 'Amazon',
  rakuten: '楽天',
};
const ASSET_CONFIG_URL = (typeof window !== 'undefined' && window.REPAIRFORM_CONFIG_URL) || '';
const PLACEHOLDER = '選択してください';
const SCROLL_OFFSET = 80;
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const AMAZON_ORDER_RE = /^\d{3}-\d{7}-\d{7}$/;
const DATE_FIELDS = ['rental_date', 'occurrence_date'];
const CHECKBOX_FIELDS = ['intentional_damage', 'truthful_description'];
const DISPLAY_TEXT_FIELDS = ['name', 'phone', 'email', 'defect_content', 'discovery_situation', 'defect_location', 'other', 'return_address', 'device_id', 'serial_number', 'ordernumber', 'file_urls'];
const DEVICE_ID_CATEGORIES = new Set(['electric', 'behavior', 'connect', 'other']);
// 製造番号はトイレ本体にのみ記載があるため、対象モデルを限定する
const SERIAL_NUMBER_MODELS = new Set(['TOL1ALL', 'TOL2ALL', 'TOL3ALL']);

/* ── ユーティリティ ── */
function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function isVideoUrl(src) {
  return /\.(mp4|webm|mov)(\?|$)/i.test(src || '');
}

function isSelected(v) {
  return !!v && v !== PLACEHOLDER;
}

function resolveAcceptFromAttachLabel(attachLabel) {
  const hasVideo = !!attachLabel && attachLabel.includes('動画');
  const hasPhoto = !!attachLabel && (attachLabel.includes('写真') || attachLabel.includes('画像'));
  if (hasVideo && !hasPhoto) return { accept: 'video/*,.mov,.mp4,.webm', allowImage: false, allowVideo: true };
  if (hasPhoto && !hasVideo) return { accept: 'image/*', allowImage: true, allowVideo: false };
  return { accept: 'image/*,video/*,.mov,.mp4,.webm', allowImage: true, allowVideo: true };
}

function isFileTypeAllowed(file, allowImage, allowVideo) {
  if (allowImage && allowVideo) return true;
  const mime = file.type;
  const name = file.name.toLowerCase();
  if (allowVideo) return mime.startsWith('video/') || /\.(mov|mp4|webm)$/.test(name);
  if (allowImage) return mime.startsWith('image/');
  return false;
}

function nextFrame(fn) {
  requestAnimationFrame(() => requestAnimationFrame(fn));
}

function scrollToEl(el) {
  if (!el) return;
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return;
  const y = el.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET;
  window.scrollTo({ top: y, behavior: 'smooth' });
}

function buildSelectHtml(items, valueFn, labelFn) {
  return `<option value="">${PLACEHOLDER}</option>` +
    items.map((item) => `<option value="${escapeHtml(valueFn(item))}">${escapeHtml(labelFn(item))}</option>`).join('');
}

function normalizeMedia(src, opts = {}) {
  if (!src) return null;
  const type = opts.type || (isVideoUrl(src) ? 'video' : 'image');
  return { type, src, poster: opts.poster || '', alt: opts.alt || '' };
}

function normalizeCheck(ch, index) {
  if (typeof ch === 'string') {
    return { id: String(index + 1), text: ch, media: null, answer: '' };
  }
  const src = ch.video || ch.media?.src || ch.img || ch.image || '';
  let media = null;
  if (ch.media) {
    media = ch.media.type
      ? ch.media
      : normalizeMedia(ch.media.src || src, { poster: ch.media.poster, alt: ch.media.alt || ch.text });
  } else if (src) {
    media = normalizeMedia(src, { poster: ch.poster, alt: ch.alt || ch.text });
  }
  return {
    id: String(ch.id ?? index + 1),
    text: ch.text || '',
    media,
    answer: ch.answer || '',
  };
}

function renderMediaHtml(media, className, { controls = true } = {}) {
  if (!media?.src) return '';
  if (media.type === 'video' || isVideoUrl(media.src)) {
    const playbackAttr = controls ? ' controls' : ' muted playsinline';
    return `<video class="${className}"${playbackAttr} preload="metadata"${media.poster ? ` poster="${escapeHtml(media.poster)}"` : ''}><source src="${escapeHtml(media.src)}"></video>`;
  }
  return `<img class="${className}" src="${escapeHtml(media.src)}" alt="${escapeHtml(media.alt || '参考画像')}" loading="lazy">`;
}

/* ================================================================
 * repairform-config.json ヘルパー
 * ================================================================ */
function getModelFlow(config, modelId) {
  const flows = config.flow || {};
  const exact = flows[modelId];
  const def = flows._default || {};
  if (!exact) return def;
  const merged = { ...def, ...exact };
  if (def.categories && !exact.categories) merged.categories = def.categories;
  if (def.issues || exact.issues) {
    const out = {};
    for (const cat of Object.keys({ ...(def.issues || {}), ...(exact.issues || {}) })) {
      out[cat] = { ...(def.issues?.[cat] || {}), ...(exact.issues?.[cat] || {}) };
    }
    merged.issues = out;
  }
  return merged;
}

function getCategories(config, modelId) {
  return getModelFlow(config, modelId).categories || [];
}

function getIssues(config, modelId, catId) {
  const g = (getModelFlow(config, modelId).issues || {})[catId] || {};
  return Object.entries(g).map(([id, meta]) => ({
    id,
    label: meta.label || id,
    img: meta.img,
    attachLabel: meta.attachLabel,
    samples: meta.samples || [],
  }));
}

function getIssueMeta(config, modelId, catId, issueId) {
  return ((getModelFlow(config, modelId).issues || {})[catId] || {})[issueId] || null;
}

/* ================================================================
 * RepairContactPage
 * ================================================================ */
class RepairContactPage {
  constructor() {
    this.$page = $('.contactPage.--repair');
    if (!this.$page.length) return;

    this.$phase1Content = this.$page.find('[data-phase="1"]');
    this.$inputContent = this.$page.find('[data-phase="2"]');
    this.$confirmContent = this.$page.find('[data-state="confirm"]');
    this.$completeContent = this.$page.find('[data-state="complete"]');

    this.$form = this.$page.find('#repairForm');
    this.$confirmButton = this.$inputContent.find('.--button .c-button');
    this.$editButton = this.$confirmContent.find('.--button .c-button').eq(0);
    this.$submitButton = this.$confirmContent.find('.--button .c-button').eq(1);

    this.displayData = {};
    this.formData = {};
    this.config = { models: [], flow: {} };
    this.uploadedFileUrls = [];
    this.uploadedTokens = [];
    this.phase1Selection = null;
    this._fileAcceptState = null;
    this._guideSteps = [];
    this._completedStepIds = new Set();
    this._guideStepAnswers = new Map();

    this.init();
  }

  async init() {
    await this.loadConfig();
    this.renderPhase1ModelCards();
    this.renderStoreOptions();
    this.bindPhase1Cascade();
    this.bindPhaseTransitions();
    this.bindStoreToggle();
    this.bindFileUpload();
    this.bindEvents();
    this.updateContactButtonState();
  }

  async loadConfig() {
    if (!ASSET_CONFIG_URL) return;
    try {
      const url = ASSET_CONFIG_URL + (ASSET_CONFIG_URL.includes('?') ? '&' : '?') + 'cb=' + Date.now();
      const r = await fetch(url, { cache: 'no-store' });
      if (!r.ok) return;
      this.config = await r.json();
    } catch (e) {
      console.warn('[repair] config load failed', e);
    }
  }

  /* ── Phase 1: モデルカード ── */
  renderPhase1ModelCards() {
    const mount = document.getElementById('phase1-model-radios');
    if (!mount) return;
    const models = Array.isArray(this.config.models) ? this.config.models : [];
    if (!models.length) {
      mount.innerHTML = '<p style="color:#6A676B;font-size:14px;">モデル情報の読み込みに失敗しました。ページを再読み込みしてください。</p>';
      return;
    }

    mount.innerHTML = models.map((m) => {
      const id = `repair_model_${m.id}`;
      return `<input type="radio" name="repair_model" id="${id}" value="${m.id}">
        <label for="${id}"><span>${escapeHtml(m.label || m.id)}</span></label>`;
    }).join('');

    mount.querySelectorAll('label').forEach((label) => {
      const radioId = label.getAttribute('for');
      const m = models.find((x) => `repair_model_${x.id}` === radioId);
      if (m && m.img) {
        label.style.setProperty('--model-img', `url('${m.img}')`);
      }
    });
  }

  /* ── Phase 2: 購入サイトラジオ ── */
  renderStoreOptions() {
    const mount = document.getElementById('repair-store-radios');
    if (!mount) return;
    mount.innerHTML = Object.entries(STORE_LABELS).map(([value, label]) => `
      <li>
        <div class="contactForm__checkbox repairStoreOption">
          <input type="radio" name="store" id="store_${value}" value="${value}">
          <label for="store_${value}">${escapeHtml(label)}</label>
        </div>
      </li>
    `).join('');
  }

  /* ── Phase 1: カスケード ── */
  bindPhase1Cascade() {
    this.$phase1Content.on('change', 'input[name="repair_model"]', () => {
      const modelId = this.getSelectedPhase1ModelId();
      this.populatePhase1CategorySelect(modelId);
      this.$phase1Content.find('[data-fields="repair_category"]').addClass('--visible');
      this.resetCascadeBelow('category');
      nextFrame(() => this.scrollToPhase1Field('[data-fields="repair_category"]'));
    });

    $('#repair_category').on('change', () => {
      const modelId = this.getSelectedPhase1ModelId();
      const catId = $('#repair_category').val();
      if (isSelected(catId)) {
        this.populatePhase1IssueSelect(modelId, catId);
        this.$phase1Content.find('[data-fields="repair_issue"]').addClass('--visible');
        nextFrame(() => this.scrollToPhase1Field('[data-fields="repair_issue"]'));
      } else {
        this.$phase1Content.find('[data-fields="repair_issue"]').removeClass('--visible');
        $('#repair_issue').html(buildSelectHtml([], (x) => x, (x) => x));
      }
      this.hideGuideSteps();
      this.phase1Selection = null;
      this.updateContactButtonState();
    });

    $('#repair_issue').on('change', () => this.onPhase1IssueSelected());
  }

  resetCascadeBelow(level) {
    if (level === 'category') {
      this.$phase1Content.find('[data-fields="repair_issue"]').removeClass('--visible');
      $('#repair_issue').html(buildSelectHtml([], (x) => x, (x) => x));
    }
    this.hideGuideSteps();
    this.phase1Selection = null;
    this.updateContactButtonState();
  }

  updateContactButtonState() {
    const modelId = this.getSelectedPhase1ModelId();
    const catId = $('#repair_category').val();
    const issueId = $('#repair_issue').val();
    const allSelected = isSelected(modelId) && isSelected(catId) && isSelected(issueId);
    const allChecksDone = this._guideSteps.length === 0 || this._completedStepIds.size === this._guideSteps.length;
    const $actions = this.$phase1Content.find('.repairPhase1__actions');
    const wasVisible = !$actions.prop('hidden');
    if (allSelected && allChecksDone) {
      $actions.prop('hidden', false);
      if (!wasVisible) {
        nextFrame(() => this.scrollToPhase1Field('.repairPhase1__actions'));
      }
    } else {
      $actions.prop('hidden', true);
    }
  }

  scrollToPhase1Field(selector) {
    scrollToEl(this.$phase1Content.find(selector)[0]);
  }

  getSelectedPhase1ModelId() {
    return this.$phase1Content.find('input[name="repair_model"]:checked').val() || '';
  }

  populatePhase1CategorySelect(modelId) {
    const cats = getCategories(this.config, modelId);
    $('#repair_category').html(buildSelectHtml(cats, (c) => c.id, (c) => c.label || c.id));
  }

  populatePhase1IssueSelect(modelId, catId) {
    const $issue = $('#repair_issue');
    if (!modelId || !catId) {
      $issue.html(buildSelectHtml([], (x) => x, (x) => x));
      return;
    }
    const issues = getIssues(this.config, modelId, catId);
    $issue.html(buildSelectHtml(issues, (i) => i.id, (i) => i.label || i.id));
  }

  onPhase1IssueSelected() {
    const modelId = this.getSelectedPhase1ModelId();
    const catId = $('#repair_category').val();
    const issueId = $('#repair_issue').val();

    if (!modelId || !catId || !issueId) {
      this.hideGuideSteps();
      this.phase1Selection = null;
      this.updateContactButtonState();
      return;
    }

    const meta = getIssueMeta(this.config, modelId, catId, issueId);
    this.phase1Selection = { modelId, catId, issueId, meta };

    if (meta) {
      this.renderGuideSteps(meta);
    } else {
      this.hideGuideSteps();
    }

    this.updateContactButtonState();
  }

  /* ── Phase 1: 確認事項ステップ ── */
  renderGuideSteps(meta) {
    const container = document.getElementById('repairGuideSteps');
    if (!container) return;

    this.resetGuideStepState();
    this._guideSteps = (meta.checks || []).map(normalizeCheck).filter((step) => step.text || step.media?.src);

    if (this._guideSteps.length === 0) {
      container.innerHTML = `
        <div class="repairGuideSteps__header">
          <span class="repairGuideSteps__title">確認事項</span>
        </div>
        <p class="repairGuideSteps__empty">この故障内容の確認事項はありません。そのままお問い合わせください。</p>
      `;
      container.hidden = false;
      this.updateContactButtonState();
      return;
    }

    container.innerHTML = `
      <div class="repairGuideSteps__header">
        <span class="repairGuideSteps__title">確認事項</span>
      </div>
      <div class="repairGuideSteps__list" id="repairGuideStepsList"></div>
    `;
    container.hidden = false;

    this.renderGuideStep(0);
    this.updateContactButtonState();
  }

  resetGuideStepState() {
    this._guideSteps = [];
    this._completedStepIds = new Set();
    this._guideStepAnswers = new Map();
  }

  renderGuideStep(index) {
    const list = document.getElementById('repairGuideStepsList');
    const step = this._guideSteps[index];
    if (!list || !step) return;

    const stepId = this.getGuideStepId(step, index);
    list.insertAdjacentHTML('beforeend', this.buildGuideStepHtml(step, index, stepId));

    const stepEl = list.querySelector(`[data-step-idx="${index}"]`);
    if (!stepEl) return;

    stepEl.querySelectorAll('input').forEach((input) => {
      input.addEventListener('change', () => {
        stepEl.classList.remove('--error');
        const err = stepEl.querySelector('.repairGuideStep__error');
        if (err) err.textContent = '';
      });
    });
    stepEl.querySelector('.repairGuideStep__next')?.addEventListener('click', () => this.completeGuideStep(index));
  }

  getGuideStepId(step, index) {
    return `${index + 1}-${step.id || index + 1}`;
  }

  buildGuideStepHtml(step, index, stepId) {
    const isYesNo = String(step.answer || '').toLowerCase() === 'yesno';
    const total = this._guideSteps.length;
    const isLast = index === total - 1;
    const escapedStepId = escapeHtml(stepId);
    const answerHtml = isYesNo
      ? `
        <div class="repairGuideStep__yn" role="radiogroup" aria-label="はい・いいえを選択">
          <label class="repairGuideStep__ynOption">
            <input type="radio" name="repair_guide_${escapedStepId}" value="はい">
            <span>はい</span>
          </label>
          <label class="repairGuideStep__ynOption">
            <input type="radio" name="repair_guide_${escapedStepId}" value="いいえ">
            <span>いいえ</span>
          </label>
        </div>
      `
      : `
        <label class="repairGuideStep__confirm">
          <input type="checkbox" name="repair_guide_${escapedStepId}" value="確認した">
          <span>確認した</span>
        </label>
      `;
    const mediaHtml = step.media?.src
      ? renderMediaHtml(step.media, 'repairGuideStep__media', {
          controls: step.media.type === 'video' || isVideoUrl(step.media.src),
        })
      : '';

    return `
      <div class="repairGuideStep --active" data-step-idx="${index}" data-step-id="${escapedStepId}">
        <div class="repairGuideStep__meta">確認 ${index + 1} / ${total}</div>
        <div class="repairGuideStep__row">
          <span class="repairGuideStep__text">${escapeHtml(step.text || '')}</span>
          ${answerHtml}
        </div>
        ${mediaHtml ? `<div class="repairGuideStep__mediaWrapper">${mediaHtml}</div>` : ''}
        <div class="repairGuideStep__actions">
          <button class="repairGuideStep__next" type="button">${isLast ? '完了' : '次へ'}</button>
          <span class="repairGuideStep__error" aria-live="polite"></span>
        </div>
      </div>
    `;
  }

  completeGuideStep(index) {
    const step = this._guideSteps[index];
    const stepEl = document.querySelector(`[data-step-idx="${index}"]`);
    if (!step || !stepEl) return;

    const isYesNo = String(step.answer || '').toLowerCase() === 'yesno';
    const answer = isYesNo
      ? stepEl.querySelector('input[type="radio"]:checked')?.value
      : (stepEl.querySelector('input[type="checkbox"]')?.checked ? true : null);

    if (!answer) {
      stepEl.classList.add('--error');
      const err = stepEl.querySelector('.repairGuideStep__error');
      if (err) err.textContent = isYesNo ? '回答を選んでください' : 'チェックを入れてください';
      stepEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const stepId = this.getGuideStepId(step, index);
    this._completedStepIds.add(stepId);
    if (isYesNo) {
      this._guideStepAnswers.set(step.text, answer);
    }
    stepEl.classList.remove('--active', '--error');
    stepEl.classList.add('--done');
    stepEl.querySelectorAll('input, button').forEach((el) => { el.disabled = true; });
    const err = stepEl.querySelector('.repairGuideStep__error');
    if (err) err.textContent = '';

    if (index < this._guideSteps.length - 1) {
      this.renderGuideStep(index + 1);
      setTimeout(() => {
        const nextEl = document.querySelector(`[data-step-idx="${index + 1}"]`);
        scrollToEl(nextEl);
      }, 50);
    }
    this.updateContactButtonState();
  }

  hideGuideSteps() {
    const container = document.getElementById('repairGuideSteps');
    if (container) {
      container.hidden = true;
      container.innerHTML = '';
    }
    this.resetGuideStepState();
  }

  /* ── フェーズ遷移 ── */
  bindPhaseTransitions() {
    $(document).on('click', '#btn-go-contact', () => this.transitionToPhase2());
    $(document).on('click', '#btn-back-to-phase1', () => this.transitionToPhase1());
  }

  transitionToPhase2() {
    if (!this.phase1Selection) return;
    const { modelId, catId, issueId, meta } = this.phase1Selection;

    $('#hidden_repair_model').val(modelId);
    $('#hidden_repair_category').val(catId);
    $('#hidden_repair_issue').val(issueId);

    const labels = this.getSelectionLabels();
    $('#summary-model').text(labels.modelLabel);
    $('#summary-category').text(labels.categoryLabel);
    $('#summary-issue').text(labels.issueLabel);

    if (meta) {
      const attachLabel = meta.attachLabel || '故障の状況が分かる写真・動画';
      $('#repair-attach-label').text(attachLabel);
      $('#repair-attach-desc').text(`${attachLabel}を添付してください。`);
      const acceptState = resolveAcceptFromAttachLabel(meta.attachLabel);
      this._fileAcceptState = acceptState;
      $('#repair_file_uploader').attr('accept', acceptState.accept);
    } else {
      this._fileAcceptState = null;
      $('#repair_file_uploader').attr('accept', 'image/*,video/*,.mov,.mp4,.webm');
    }

    const $deviceIdField = this.$form.find('[data-fields="device_id"]');
    if (DEVICE_ID_CATEGORIES.has(catId)) {
      $deviceIdField.addClass('--visible --require');
      $deviceIdField.find('.errorMsg').hide();
    } else {
      $deviceIdField.removeClass('--visible --require');
      $deviceIdField.find('input').val('');
      $deviceIdField.find('.errorMsg').hide();
    }

    const $serialNumberField = this.$form.find('[data-fields="serial_number"]');
    if (SERIAL_NUMBER_MODELS.has(modelId)) {
      $serialNumberField.addClass('--visible');
    } else {
      $serialNumberField.removeClass('--visible');
      $serialNumberField.find('input').val('');
    }

    this.$phase1Content.hide();
    this.$inputContent.show();
    window.scrollTo(0, 0);
  }

  transitionToPhase1() {
    this._fileAcceptState = null;
    $('#repair_file_uploader').attr('accept', 'image/*,video/*,.mov,.mp4,.webm');
    this.$inputContent.hide();
    this.$phase1Content.show();
    window.scrollTo(0, 0);
  }

  /* ── Phase 2: 購入サイトトグル ── */
  bindStoreToggle() {
    const $orderField = this.$form.find('[data-fields="ordernumber"]');
    const toggle = () => {
      const store = this.$form.find('input[name="store"]:checked').val();
      if (store === 'amazon') {
        $orderField.addClass('--visible --require');
      } else {
        $orderField.removeClass('--visible --require');
        $('#ordernumber').val('');
        $orderField.find('.errorMsg').hide();
      }
    };
    this.$form.on('change', 'input[name="store"]', toggle);
    toggle();
  }

  /* ── Phase 2: ファイルアップロード ── */
  bindFileUpload() {
    const $btn = $('#repair_upload_btn');
    const $input = $('#repair_file_uploader');
    const $hidden = $('#contact_attachment');
    const $list = $('#repair_uploaded_list');

    $btn.on('click', () => $input.trigger('click'));

    $input.on('change', async (e) => {
      const files = [...(e.target.files || [])];
      if (!files.length) return;

      const workerUrl = mirutoWorkers().zendeskFileUpload;
      if (!workerUrl) {
        alert('ファイルアップロードの設定が見つかりません。');
        return;
      }

      const { allowImage, allowVideo } = this._fileAcceptState || { allowImage: true, allowVideo: true };
      const invalid = files.filter((f) => !isFileTypeAllowed(f, allowImage, allowVideo));
      if (invalid.length) {
        const typeLabel = allowVideo && !allowImage ? '動画（.mov / .mp4 / .webm）'
          : allowImage && !allowVideo ? '画像ファイル'
          : '画像または動画';
        alert(`以下のファイルは${typeLabel}以外のため添付できません。\n${invalid.map((f) => f.name).join('\n')}`);
        $input.val('');
        return;
      }

      const MAX_FILE_SIZE = 50 * 1024 * 1024;
      const oversized = files.filter((f) => f.size > MAX_FILE_SIZE);
      if (oversized.length) {
        alert(`以下のファイルは50MBを超えているためアップロードできません。\n${oversized.map((f) => f.name).join('\n')}`);
        $input.val('');
        return;
      }

      $btn.prop('disabled', true).find('.c-button__text, .c-button__animationText').text('アップロード中…');

      try {
        for (const file of files) {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('filename', file.name);
          const r = await fetch(workerUrl, { method: 'POST', body: fd });
          const j = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(j.error || 'upload failed');
          if (Array.isArray(j.tokens)) this.uploadedTokens.push(...j.tokens);
          this.uploadedFileUrls.push(file.name);
        }
        this.renderUploadedFileList($hidden, $list);
        $hidden.closest('.contactForm__item').find('.errorMsg').hide();
      } catch (uploadErr) {
        console.error('[repair] upload failed', uploadErr);
        alert('ファイルのアップロードに失敗しました。もう一度お試しください。');
      } finally {
        $btn.prop('disabled', false).find('.c-button__text, .c-button__animationText').text('ファイルを追加する');
        $input.val('');
      }
    });

    $list.on('click', '.repairUploadedFile__remove', (e) => {
      const idx = parseInt($(e.currentTarget).data('index'), 10);
      this.uploadedFileUrls.splice(idx, 1);
      this.uploadedTokens.splice(idx, 1);
      this.renderUploadedFileList($hidden, $list);
    });
  }

  renderUploadedFileList($hidden, $list) {
    $hidden.val(this.uploadedFileUrls.join(', '));
    if (!this.uploadedFileUrls.length) {
      $list.html('').prop('hidden', true);
      return;
    }
    $list.html(
      this.uploadedFileUrls.map((name, i) =>
        `<li class="repairUploadedFile">
          <span>${escapeHtml(name)}</span>
          <button type="button" class="repairUploadedFile__remove" data-index="${i}" aria-label="削除">×</button>
        </li>`
      ).join('')
    ).prop('hidden', false);
  }

  /* ── イベント ── */
  bindEvents() {
    this.$form.on('submit', (e) => e.preventDefault());
    this.$confirmButton.on('click', () => this.handleConfirm());
    this.$editButton.on('click', () => this.handleEdit());
    this.$submitButton.on('click', () => this.handleSubmit());

    this.$form.find('input[type="checkbox"]').on('change', function () {
      $(this).closest('.contactForm__item').find('.errorMsg').hide();
    });

    const bindGuideOverlay = (linkId, overlayId) => {
      const $overlay = $('#' + overlayId);
      if (!$overlay.length) return;
      let _triggerEl = null;
      const openOverlay = (trigger) => {
        _triggerEl = trigger || null;
        $overlay.css('display', 'flex').attr('aria-hidden', 'false');
        $overlay[0].focus();
      };
      const closeOverlay = () => {
        $overlay.hide().attr('aria-hidden', 'true');
        if (_triggerEl) { _triggerEl.focus(); _triggerEl = null; }
      };
      $('#' + linkId).on('click', function (e) {
        e.preventDefault();
        openOverlay(this);
      });
      $overlay.on('click', (e) => { if (e.target === $overlay[0]) closeOverlay(); });
      $(document).on('keydown', (e) => {
        if (e.key === 'Escape' && $overlay.is(':visible')) closeOverlay();
      });
    };
    bindGuideOverlay('deviceIdGuideLink', 'deviceIdGuideOverlay');
    bindGuideOverlay('serialNumberGuideLink', 'serialNumberGuideOverlay');
  }

  /* ── バリデーション（Phase 2 フォームのみ） ── */
  validateRequiredFields() {
    let isValid = true;
    this.$form.find('.--require').each((_, field) => {
      if (!this.validateField($(field))) isValid = false;
    });
    return isValid;
  }

  validateField($field) {
    if (!$field.is(':visible')) return true;

    const $input = $field.find('input, textarea, select').first();
    if ($input.prop('readonly') && ($input.val() || '').trim()) return true;

    const $errorMsg = $field.find('.errorMsg');

    if ($field.data('fields') === 'file_urls') {
      if (!this.uploadedFileUrls.length) {
        $errorMsg.css('display', 'block');
        return false;
      }
      $errorMsg.hide();
      return true;
    }

    const $radios = $field.find('input[type="radio"]');

    if ($radios.length) {
      return this.validateRadioGroup($radios, $errorMsg);
    }

    const inputType = ($input.attr('type') || $input.prop('tagName') || '').toLowerCase();
    if (inputType === 'hidden') return true;

    const value = ($input.val() || '').trim();
    const nameAttr = $input.attr('name');

    if (inputType === 'checkbox') return this.validateCheckbox($input, $errorMsg);
    if (inputType === 'email') return this.validateEmail($input, value, $errorMsg);
    if ($input.is('select')) return this.validateSelect(value, $errorMsg);
    if (nameAttr === 'ordernumber') return this.validateOrderNumber(value, $errorMsg);
    return this.validateText(value, nameAttr, $errorMsg);
  }

  validateRadioGroup($radios, $errorMsg) {
    if ($radios.filter(':checked').length > 0) {
      $errorMsg.hide();
      return true;
    }
    $errorMsg.css('display', 'block');
    return false;
  }

  validateCheckbox($input, $errorMsg) {
    if ($input.prop('checked')) {
      $errorMsg.hide();
      return true;
    }
    $errorMsg.css('display', 'block');
    return false;
  }

  validateEmail($input, value, $errorMsg) {
    if ($input.attr('id') === 'contact_email_confirm') {
      const mainEmail = $('#contact_email').val()?.trim();
      if (!value) {
        $errorMsg.text('※メールアドレスを入力してください').css('display', 'block');
        return false;
      }
      if (!EMAIL_RE.test(value)) {
        $errorMsg.text('有効なメールアドレスを入力してください').css('display', 'block');
        return false;
      }
      if (value !== mainEmail) {
        $errorMsg.text('メールアドレスが一致しません').css('display', 'block');
        return false;
      }
      $errorMsg.hide();
      return true;
    }
    if (!EMAIL_RE.test(value)) {
      $errorMsg.text('有効なメールアドレスを入力してください').css('display', 'block');
      return false;
    }
    $errorMsg.hide();
    return true;
  }

  validateSelect(value, $errorMsg) {
    if (!isSelected(value)) {
      $errorMsg.css('display', 'block');
      return false;
    }
    $errorMsg.hide();
    return true;
  }

  validateOrderNumber(value, $errorMsg) {
    const store = this.$form.find('input[name="store"]:checked').val();
    if (store !== 'amazon') return true;
    if (!value) {
      $errorMsg.css('display', 'block');
      return false;
    }
    if (!AMAZON_ORDER_RE.test(value)) {
      $errorMsg.text('注文番号は「123-1234567-1234567」の形式で入力してください').css('display', 'block');
      return false;
    }
    $errorMsg.hide();
    return true;
  }

  validateText(value, nameAttr, $errorMsg) {
    if (!value) {
      $errorMsg.css('display', 'block');
      return false;
    }
    if ((nameAttr === 'defect_content' || nameAttr === 'discovery_situation') && value.length < 10) {
      $errorMsg.text('10文字以上で入力してください').css('display', 'block');
      return false;
    }
    if (nameAttr === 'device_id' && !/^[a-zA-Z0-9]+$/.test(value)) {
      $errorMsg.text('半角英数字で入力してください').css('display', 'block');
      return false;
    }
    $errorMsg.hide();
    return true;
  }

  /* ── Phase 1 選択のラベル取得 ── */
  getSelectionLabels() {
    const { modelId, catId, issueId } = this.phase1Selection || {};
    const model = (this.config.models || []).find((m) => m.id === modelId);
    const cat = getCategories(this.config, modelId || '').find((c) => c.id === catId);
    const issue = getIssues(this.config, modelId || '', catId || '').find((i) => i.id === issueId);
    return {
      modelId: modelId || '',
      modelLabel: model ? (model.label || modelId) : (modelId || ''),
      categoryLabel: cat ? (cat.label || catId) : (catId || ''),
      issueLabel: issue ? (issue.label || issueId) : (issueId || ''),
    };
  }

  /* ── フォームデータ収集 ── */
  collectFormData() {
    const labels = this.getSelectionLabels();
    this.displayData = {};
    this.formData = {};

    const fd = new FormData(this.$form[0]);
    for (const [key, value] of fd.entries()) {
      if (key === 'email_confirm' || key === 'repair_file_uploader') continue;
      if (value instanceof File) continue;
      this.formData[key] = value;
    }

    this.displayData.repair_model = labels.modelLabel;
    this.displayData.repair_category = labels.categoryLabel;
    this.displayData.repair_issue = labels.issueLabel;

    DATE_FIELDS.forEach((key) => {
      const v = this.formData[key];
      if (v && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
        const [y, m, d] = v.split('-');
        this.displayData[key] = `${y}/${m}/${d}`;
      }
    });

    CHECKBOX_FIELDS.forEach((key) => {
      const $cb = this.$form.find(`[name="${key}"]`);
      if ($cb.prop('checked')) {
        this.displayData[key] = $cb.next('label').text() || 'はい';
        this.formData[key] = true;
      }
    });

    const storeVal = this.$form.find('input[name="store"]:checked').val() || '';
    this.displayData.store = STORE_LABELS[storeVal] || storeVal;
    this.formData.store = storeVal;

    DISPLAY_TEXT_FIELDS.forEach((key) => {
      if (this.formData[key]) this.displayData[key] = this.formData[key];
    });

    this.formData._modelId = labels.modelId;
    this.formData._modelLabel = labels.modelLabel;
    this.formData._categoryLabel = labels.categoryLabel;
    this.formData._issueLabel = labels.issueLabel;
  }

  displayConfirmData() {
    this.$confirmContent.find('.contactForm__item[data-fields]').each((_, field) => {
      const $field = $(field);
      const fieldName = $field.data('fields');
      const $content = $field.find('.confirm_content');
      if (fieldName === 'file_urls') {
        if (this.uploadedFileUrls.length) {
          $content.html(
            `<ul class="confirmFileList">${this.uploadedFileUrls.map((name) => `<li>${escapeHtml(name)}</li>`).join('')}</ul>`
          );
          $field.show();
        } else {
          $field.hide();
        }
        return;
      }
      if (fieldName && this.displayData[fieldName]) {
        $content.text(this.displayData[fieldName]);
        $field.show();
      } else {
        $field.hide();
      }
    });
  }

  /* ── Zendesk 送信 ── */
  buildDiagnosticChecksString() {
    if (this._guideStepAnswers.size === 0) return '';
    return Array.from(this._guideStepAnswers.entries())
      .map(([text, ans]) => `${text}: ${ans}`)
      .join('\n');
  }

  buildZendeskPayload() {
    const f = this.formData;
    const labels = this.getSelectionLabels();
    const storeLabel = STORE_LABELS[f.store] || f.store || '';

    const inquiryParts = [
      `カテゴリ: ${labels.categoryLabel}`,
      `故障内容: ${labels.issueLabel}`,
      storeLabel ? `購入サイト: ${storeLabel}` : '',
      f.ordernumber ? `注文番号: ${f.ordernumber}` : '',
    ].filter(Boolean);

    return {
      name: f.name || '',
      email: f.email || '',
      phone: f.phone || '',
      productName: labels.modelLabel,
      defectContent: f.defect_content || '',
      defectSituation: f.discovery_situation || '',
      defectLocation: f.defect_location || '',
      intentionalDamage: f.intentional_damage ?? false,
      specificCheck: f.truthful_description ?? false,
      rentalDate: f.rental_date || '',
      birthday: f.occurrence_date || '',
      other: f.other || '',
      returnAddress: f.return_address || '',
      deviceId: f.device_id || '',
      serialNumber: f.serial_number || '',
      inquiry: inquiryParts.join('\n'),
      diagnosticChecks: this.buildDiagnosticChecksString(),
      faultCategory: labels.categoryLabel,
      faultContent: labels.issueLabel,
      uploads: this.uploadedTokens,
    };
  }

  async submitToZendesk() {
    const endpoint = mirutoWorkers().productFailureFormSubmit;
    if (!endpoint) {
      console.error('productFailureFormSubmit not configured');
      return false;
    }
    try {
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.buildZendeskPayload()),
      });
      if (!resp.ok) {
        console.error('Zendesk worker error:', resp.status);
        return false;
      }
      return true;
    } catch (e) {
      console.error('Zendesk submission error:', e);
      return false;
    }
  }

  /* ── フォームフロー ── */
  handleConfirm() {
    const $buttonErrorMsg = this.$inputContent.find('.--button .errorMsg');
    if (!this.validateRequiredFields()) {
      $buttonErrorMsg.css('display', 'block');
      return;
    }
    $buttonErrorMsg.hide();
    this.collectFormData();
    this.displayConfirmData();
    this.$inputContent.hide();
    this.$confirmContent.show();
    window.scrollTo(0, 0);
  }

  handleEdit() {
    this.$confirmContent.hide();
    this.$inputContent.show();
    window.scrollTo(0, 0);
  }

  async handleSubmit() {
    this.$submitButton.prop('disabled', true);
    const success = await this.submitToZendesk();
    this.$submitButton.prop('disabled', false);

    if (success) {
      this.$confirmContent.hide();
      this.$completeContent.show();
      window.scrollTo(0, 0);
      this.$form[0].reset();
      this.uploadedFileUrls = [];
      this.uploadedTokens = [];
      this.hideGuideSteps();
    } else {
      alert('送信に失敗しました。もう一度お試しください。');
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new RepairContactPage();
});
